import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { DraggableBounds } from 'react-draggable';
import Draggable from 'react-draggable';

import { ArrowSlimIcon } from '@/assets/icons/v2/ArrowSlimIcon';
import { BOTTOM_MARGIN, MINIMIZED_PLAYER_HEIGHT } from '../../utils/constants';

import * as S from '../../styles';

interface CollapsedProps {
	onExpand?: () => void;
	onResize?: () => void;
}

function calculateMaxUpwardDrag(): number {
	const verticalFreeSpace = window.innerHeight - MINIMIZED_PLAYER_HEIGHT - BOTTOM_MARGIN;
	return -Math.max(verticalFreeSpace, 0);
}

function updateBounds(setBounds: (bounds: DraggableBounds) => void): void {
	setBounds({
		left: 0,
		right: 0,
		top: calculateMaxUpwardDrag(),
		bottom: 0
	});
}

export function Collapsed({ onExpand, onResize }: Readonly<CollapsedProps>): JSX.Element {
	const [disabled, setDisabled] = useState<boolean>(true);
	const [bounds, setBounds] = useState<DraggableBounds | undefined>(undefined);
	const draggableRef = useRef<HTMLDivElement>(null);

	const handleMouseOver = useCallback(() => {
		setDisabled(false);
	}, []);

	const handleMouseOut = useCallback(() => {
		setDisabled(true);
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') {
			setBounds(undefined);
			return;
		}

		function handleResize(): void {
			updateBounds(setBounds);
		}

		updateBounds(setBounds);
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	return (
		<S.MinimizedPlayerContainer>
			<Draggable axis="y" bounds={bounds} disabled={disabled} nodeRef={draggableRef}>
				<div ref={draggableRef}>
					<S.MinimizedPlayer onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
						<S.MinimizedPlayerButton onClick={onExpand} aria-label="Expand video player">
							<ArrowSlimIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
						</S.MinimizedPlayerButton>
						{onResize ? (
							<S.ResizeButton onClick={onResize} aria-label="Resize video player">
								<ArrowSlimIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
							</S.ResizeButton>
						) : null}
					</S.MinimizedPlayer>
				</div>
			</Draggable>
		</S.MinimizedPlayerContainer>
	);
}
